'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type SubscriptionTier = 'basic' | 'pro' | 'enterprise';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string | null;
  companyName: string | null;
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: string;
  maxEndpoints: number;
  aiFeaturesEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { fullName?: string }) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  hasFeatureAccess: (feature: 'ai' | 'multiplatform' | 'advanced_analytics' | 'monetization') => boolean;
  getTierLabel: () => string;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const mapProfile = (data: any): UserProfile => ({
  id: data.id,
  email: data.email,
  fullName: data.full_name,
  companyName: data.company_name,
  subscriptionTier: data.subscription_tier ?? 'basic',
  subscriptionStatus: data.subscription_status ?? 'active',
  maxEndpoints: data.max_endpoints ?? 5,
  aiFeaturesEnabled: data.ai_features_enabled ?? false,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !data) return null;
      return mapProfile(data);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email: string, password: string, metadata: { fullName?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    setProfile(p);
  };

  const hasFeatureAccess = (feature: 'ai' | 'multiplatform' | 'advanced_analytics' | 'monetization'): boolean => {
    if (!profile) return false;
    const tier = profile.subscriptionTier;
    switch (feature) {
      case 'ai':
        return profile.aiFeaturesEnabled || tier === 'pro' || tier === 'enterprise';
      case 'multiplatform':
        return tier === 'pro' || tier === 'enterprise';
      case 'advanced_analytics':
        return tier === 'pro' || tier === 'enterprise';
      case 'monetization':
        return tier === 'pro' || tier === 'enterprise';
      default:
        return false;
    }
  };

  const getTierLabel = (): string => {
    if (!profile) return 'Free';
    switch (profile.subscriptionTier) {
      case 'basic': return 'Basic';
      case 'pro': return 'Pro';
      case 'enterprise': return 'Enterprise';
      default: return 'Basic';
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    hasFeatureAccess,
    getTierLabel,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
